import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Category,
  Business,
  LocationOn,
  HomeWork,
  Search,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, PermissionGate, usePermissions } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
)

// ============================================================================
// CATEGORIES TAB
// ============================================================================
const CategoriesTab = () => {
  const [items, setItems] = useState([])
  const [parentOptions, setParentOptions] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [formData, setFormData] = useState({ name: '', code: '', description: '', parent: '', is_active: true })
  const [formLoading, setFormLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { canEdit, canDelete } = usePermissions()

  const columns = useMemo(() => [
    { field: 'code', headerName: 'Code', sortable: true, width: 120 },
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    { field: 'parent_name', headerName: 'Parent', renderCell: (v) => v || '-' },
    { field: 'asset_count', headerName: 'Assets', width: 80, renderCell: (v) => v || 0 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (v) => <Chip label={v ? 'Active' : 'Inactive'} color={v ? 'success' : 'default'} size="small" />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canEdit('assets') && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete('assets') && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => { setSelected(row); setDeleteOpen(true) }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [canEdit, canDelete])

  const fetchData = async (searchTerm = search, pageIndex = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true)
      const response = await api.get('/assets/categories/', {
        params: { search: searchTerm, page: pageIndex + 1, page_size: pageSize },
      })
      setItems(response.data.results || response.data)
      setTotalCount(response.data.count ?? (response.data.results || response.data).length)
    } catch (error) {
      toast.error('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  // All categories for the Parent dropdown (not affected by table paging).
  const fetchParentOptions = async () => {
    try {
      const res = await api.get('/assets/categories/', { params: { page_size: 100 } })
      setParentOptions(res.data.results || res.data)
    } catch (error) { /* non-fatal */ }
  }

  useEffect(() => { fetchParentOptions() }, [])

  // Server-side fetch whenever search or pagination changes (debounced).
  useEffect(() => {
    const handle = setTimeout(() => fetchData(search, page, rowsPerPage), 300)
    return () => clearTimeout(handle)
  }, [search, page, rowsPerPage])

  const handleEdit = (item) => {
    setSelected(item)
    setFormData({
      name: item.name || '',
      code: item.code || '',
      description: item.description || '',
      parent: item.parent || '',
      is_active: item.is_active !== false,
    })
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Name and Code are required')
      return
    }
    setFormLoading(true)
    try {
      const payload = { ...formData, parent: formData.parent || null }
      if (selected) {
        await api.patch(`/assets/categories/${selected.id}/`, payload)
        toast.success('Category updated')
      } else {
        await api.post('/assets/categories/', payload)
        toast.success('Category created')
      }
      fetchData(search)
      fetchParentOptions()
      setFormOpen(false)
      setSelected(null)
      setFormData({ name: '', code: '', description: '', parent: '', is_active: true })
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/categories/${selected.id}/`)
      toast.success('Category deleted')
      fetchData(search)
      fetchParentOptions()
      setDeleteOpen(false)
      setSelected(null)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or code..."
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value) }}
          helperText="Type to search the full list of categories"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <PermissionGate permission="assets.create">
          <Button startIcon={<Add />} onClick={() => { setSelected(null); setFormData({ name: '', code: '', description: '', parent: '', is_active: true }); setFormOpen(true) }}>
            Add Category
          </Button>
        </PermissionGate>
      </Box>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        searchable={false}
        serverSide
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPaginationChange={({ page: newPage, pageSize }) => { setPage(newPage); setRowsPerPage(pageSize) }}
        onRowClick={(row) => canEdit('assets') && handleEdit(row)}
      />

      {/* Form Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selected ? 'Edit Category' : 'Add Category'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={formLoading}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Code" name="code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" name="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Parent Category</InputLabel>
              <Select name="parent" value={formData.parent} onChange={(e) => setFormData({ ...formData, parent: e.target.value })} label="Parent Category">
                <MenuItem value=""><em>None</em></MenuItem>
                {parentOptions.filter(c => c.id !== selected?.id).map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Category"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>Delete</Button>
          </>
        }
      >
        Are you sure you want to delete "{selected?.name}"? This action cannot be undone.
      </Modal>
    </>
  )
}

// ============================================================================
// MANUFACTURERS TAB
// ============================================================================
const ManufacturersTab = () => {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [formData, setFormData] = useState({ name: '', contact_info: '', website: '', notes: '', is_active: true })
  const [formLoading, setFormLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { canEdit, canDelete } = usePermissions()

  const columns = useMemo(() => [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'contact_info', headerName: 'Contact Info', renderCell: (v) => v || '-' },
    { field: 'website', headerName: 'Website', renderCell: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer">{v}</a> : '-' },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (v) => <Chip label={v ? 'Active' : 'Inactive'} color={v ? 'success' : 'default'} size="small" />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canEdit('assets') && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete('assets') && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => { setSelected(row); setDeleteOpen(true) }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [canEdit, canDelete])

  const fetchData = async (searchTerm = search, pageIndex = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true)
      const response = await api.get('/assets/manufacturers/', {
        params: { search: searchTerm, page: pageIndex + 1, page_size: pageSize },
      })
      setItems(response.data.results || response.data)
      setTotalCount(response.data.count ?? (response.data.results || response.data).length)
    } catch (error) {
      toast.error('Failed to load manufacturers')
    } finally {
      setLoading(false)
    }
  }

  // Server-side fetch whenever the search term or pagination changes (debounced).
  useEffect(() => {
    const handle = setTimeout(() => fetchData(search, page, rowsPerPage), 300)
    return () => clearTimeout(handle)
  }, [search, page, rowsPerPage])

  const handleEdit = (item) => {
    setSelected(item)
    setFormData({
      name: item.name || '',
      contact_info: item.contact_info || '',
      website: item.website || '',
      notes: item.notes || '',
      is_active: item.is_active !== false,
    })
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Name is required')
      return
    }
    setFormLoading(true)
    try {
      if (selected) {
        await api.patch(`/assets/manufacturers/${selected.id}/`, formData)
        toast.success('Manufacturer updated')
      } else {
        await api.post('/assets/manufacturers/', formData)
        toast.success('Manufacturer created')
      }
      fetchData(search)
      setFormOpen(false)
      setSelected(null)
      setFormData({ name: '', contact_info: '', website: '', notes: '', is_active: true })
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save manufacturer')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/manufacturers/${selected.id}/`)
      toast.success('Manufacturer deleted')
      fetchData(search)
      setDeleteOpen(false)
      setSelected(null)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete manufacturer')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value) }}
          helperText="Type to search the full list of manufacturers"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <PermissionGate permission="assets.create">
          <Button startIcon={<Add />} onClick={() => { setSelected(null); setFormData({ name: '', contact_info: '', website: '', notes: '', is_active: true }); setFormOpen(true) }}>
            Add Manufacturer
          </Button>
        </PermissionGate>
      </Box>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        searchable={false}
        serverSide
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPaginationChange={({ page: newPage, pageSize }) => { setPage(newPage); setRowsPerPage(pageSize) }}
        onRowClick={(row) => canEdit('assets') && handleEdit(row)}
      />

      {/* Form Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selected ? 'Edit Manufacturer' : 'Add Manufacturer'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={formLoading}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Contact Info" name="contact_info" value={formData.contact_info} onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Website" name="website" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://example.com" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Manufacturer"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>Delete</Button>
          </>
        }
      >
        Are you sure you want to delete "{selected?.name}"? This action cannot be undone.
      </Modal>
    </>
  )
}

// ============================================================================
// LOCATIONS TAB
// ============================================================================
const LocationsTab = () => {
  const [items, setItems] = useState([])
  const [departments, setDepartments] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [formData, setFormData] = useState({ name: '', building: '', floor: '', ward: '', department: '', description: '', is_active: true })
  const [formLoading, setFormLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { canEdit, canDelete } = usePermissions()

  const columns = useMemo(() => [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'building', headerName: 'Building', renderCell: (v) => v || '-' },
    { field: 'floor', headerName: 'Floor', width: 80, renderCell: (v) => v || '-' },
    { field: 'ward', headerName: 'Ward', renderCell: (v) => v || '-' },
    { field: 'department_name', headerName: 'Department', renderCell: (v) => v || '-' },
    { field: 'asset_count', headerName: 'Assets', width: 80, renderCell: (v) => v || 0 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (v) => <Chip label={v ? 'Active' : 'Inactive'} color={v ? 'success' : 'default'} size="small" />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canEdit('assets') && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete('assets') && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => { setSelected(row); setDeleteOpen(true) }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [canEdit, canDelete])

  const fetchData = async (searchTerm = search, pageIndex = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true)
      const locRes = await api.get('/assets/locations/', {
        params: { search: searchTerm, page: pageIndex + 1, page_size: pageSize },
      })
      setItems(locRes.data.results || locRes.data)
      setTotalCount(locRes.data.count ?? (locRes.data.results || locRes.data).length)
    } catch (error) {
      toast.error('Failed to load locations')
    } finally {
      setLoading(false)
    }
  }

  // Departments are only needed for the form's dropdown; fetch once.
  useEffect(() => {
    api.get('/common/departments/', { params: { page_size: 100 } })
      .then((res) => setDepartments(res.data.results || res.data))
      .catch(() => {})
  }, [])

  // Server-side fetch whenever the search term or pagination changes (debounced).
  useEffect(() => {
    const handle = setTimeout(() => fetchData(search, page, rowsPerPage), 300)
    return () => clearTimeout(handle)
  }, [search, page, rowsPerPage])

  const handleEdit = (item) => {
    setSelected(item)
    setFormData({
      name: item.name || '',
      building: item.building || '',
      floor: item.floor || '',
      ward: item.ward || '',
      department: item.department || '',
      description: item.description || '',
      is_active: item.is_active !== false,
    })
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Name is required')
      return
    }
    setFormLoading(true)
    try {
      const payload = { ...formData, department: formData.department || null }
      if (selected) {
        await api.patch(`/assets/locations/${selected.id}/`, payload)
        toast.success('Location updated')
      } else {
        await api.post('/assets/locations/', payload)
        toast.success('Location created')
      }
      fetchData(search)
      setFormOpen(false)
      setSelected(null)
      setFormData({ name: '', building: '', floor: '', ward: '', department: '', description: '', is_active: true })
    } catch (error) {
      const data = error.response?.data
      const fieldError = data && typeof data === 'object'
        ? data.detail || data.name?.[0] || Object.values(data)?.[0]
        : null
      toast.error(fieldError || 'Failed to save location')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/locations/${selected.id}/`)
      toast.success('Location deleted')
      fetchData(search)
      setDeleteOpen(false)
      setSelected(null)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or building..."
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value) }}
          helperText="Type to search the full list of locations"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <PermissionGate permission="assets.create">
          <Button startIcon={<Add />} onClick={() => { setSelected(null); setFormData({ name: '', building: '', floor: '', ward: '', department: '', description: '', is_active: true }); setFormOpen(true) }}>
            Add Location
          </Button>
        </PermissionGate>
      </Box>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        searchable={false}
        serverSide
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPaginationChange={({ page: newPage, pageSize }) => { setPage(newPage); setRowsPerPage(pageSize) }}
        onRowClick={(row) => canEdit('assets') && handleEdit(row)}
      />

      {/* Form Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selected ? 'Edit Location' : 'Add Location'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={formLoading}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Building" name="building" value={formData.building} onChange={(e) => setFormData({ ...formData, building: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Floor" name="floor" value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Ward" name="ward" value={formData.ward} onChange={(e) => setFormData({ ...formData, ward: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select name="department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} label="Department">
                <MenuItem value=""><em>None</em></MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" name="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Location"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>Delete</Button>
          </>
        }
      >
        Are you sure you want to delete "{selected?.name}"? This action cannot be undone.
      </Modal>
    </>
  )
}

// ============================================================================
// FACILITIES TAB
// ============================================================================
const FacilitiesTab = () => {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [formData, setFormData] = useState({ name: '', address: '', contact_phone: '', contact_email: '', description: '', is_active: true })
  const [formLoading, setFormLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { canEdit, canDelete } = usePermissions()

  const columns = useMemo(() => [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'address', headerName: 'Address', renderCell: (v) => v || '-' },
    { field: 'contact_phone', headerName: 'Phone', renderCell: (v) => v || '-' },
    { field: 'contact_email', headerName: 'Email', renderCell: (v) => v || '-' },
    { field: 'asset_count', headerName: 'Assets', width: 80, renderCell: (v) => v || 0 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (v) => <Chip label={v ? 'Active' : 'Inactive'} color={v ? 'success' : 'default'} size="small" />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canEdit('assets') && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete('assets') && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => { setSelected(row); setDeleteOpen(true) }}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [canEdit, canDelete])

  const fetchData = async (searchTerm = search, pageIndex = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true)
      const response = await api.get('/assets/facilities/', {
        params: { search: searchTerm, page: pageIndex + 1, page_size: pageSize },
      })
      setItems(response.data.results || response.data)
      setTotalCount(response.data.count ?? (response.data.results || response.data).length)
    } catch (error) {
      toast.error('Failed to load facilities')
    } finally {
      setLoading(false)
    }
  }

  // Server-side fetch whenever the search term or pagination changes (debounced).
  useEffect(() => {
    const handle = setTimeout(() => fetchData(search, page, rowsPerPage), 300)
    return () => clearTimeout(handle)
  }, [search, page, rowsPerPage])

  const handleEdit = (item) => {
    setSelected(item)
    setFormData({
      name: item.name || '',
      address: item.address || '',
      contact_phone: item.contact_phone || '',
      contact_email: item.contact_email || '',
      description: item.description || '',
      is_active: item.is_active !== false,
    })
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Name is required')
      return
    }
    setFormLoading(true)
    try {
      if (selected) {
        await api.patch(`/assets/facilities/${selected.id}/`, formData)
        toast.success('Facility updated')
      } else {
        await api.post('/assets/facilities/', formData)
        toast.success('Facility created')
      }
      fetchData(search)
      setFormOpen(false)
      setSelected(null)
      setFormData({ name: '', address: '', contact_phone: '', contact_email: '', description: '', is_active: true })
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save facility')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/facilities/${selected.id}/`)
      toast.success('Facility deleted')
      fetchData(search)
      setDeleteOpen(false)
      setSelected(null)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete facility')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or address..."
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value) }}
          helperText="Type to search the full list of facilities"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <PermissionGate permission="assets.create">
          <Button startIcon={<Add />} onClick={() => { setSelected(null); setFormData({ name: '', address: '', contact_phone: '', contact_email: '', description: '', is_active: true }); setFormOpen(true) }}>
            Add Facility
          </Button>
        </PermissionGate>
      </Box>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        searchable={false}
        serverSide
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPaginationChange={({ page: newPage, pageSize }) => { setPage(newPage); setRowsPerPage(pageSize) }}
        onRowClick={(row) => canEdit('assets') && handleEdit(row)}
      />

      {/* Form Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selected ? 'Edit Facility' : 'Add Facility'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={formLoading}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" name="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} multiline rows={2} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Phone" name="contact_phone" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Email" name="contact_email" type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" name="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Facility"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>Delete</Button>
          </>
        }
      >
        Are you sure you want to delete "{selected?.name}"? This action cannot be undone.
      </Modal>
    </>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const AssetSettings = () => {
  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  return (
    <Box>
      <PageHeader
        title="Asset Settings"
        subtitle="Manage asset categories, manufacturers, locations, and facilities"
      />

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="asset settings tabs">
            <Tab icon={<Category />} iconPosition="start" label="Categories" />
            <Tab icon={<Business />} iconPosition="start" label="Manufacturers" />
            <Tab icon={<LocationOn />} iconPosition="start" label="Locations" />
            <Tab icon={<HomeWork />} iconPosition="start" label="Facilities" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <CategoriesTab />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <ManufacturersTab />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <LocationsTab />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <FacilitiesTab />
        </TabPanel>
      </Card>
    </Box>
  )
}

export default AssetSettings
